'use strict';

// external imports
import React, {
    useCallback,
    useLayoutEffect,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';
import {enumerate} from 'ts-transformer-enumerate';
import moment from 'moment-timezone';
import {XMLValidator} from 'fast-xml-parser';

// prime react imports
import {Button} from 'primereact/button';
import {Card} from 'primereact/card';
import {Dialog} from 'primereact/dialog';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import {XmlParser} from '~/shared/src/utilities/XmlParser';

// frontend imports
import InputToolbar from '~/frontend/src/components/base/XmlImport/Input/Toolbar';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    AllProps as ParentAllProps,
    ComponentProps as ParentComponentProps
} from '~/frontend/src/modules/panels/XmlImport';
import {nl2br} from '~/frontend/src/utilities/renderHelpers';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {
    setElusiveData as setElusiveDataOrig,
    unsetElusiveData as unsetElusiveDataOrig
} from '~/frontend/src/actions/elusiveData';
import setParamOrig from '~/frontend/src/actions/panel/common/setParam';


export type InputType = 'authorization' | 'metaModel' | 'data';
export const inputTypes = enumerate<InputType>();
export const schemata = enumerate<YnSchema>();
schemata.sort();

type PanelElusiveData = PanelErrorData & { xml : string; };

export type DefaultProps = {};
export const defaultProps : Required<DefaultProps> = {};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    value : Required<ParentComponentProps>['inputValue'];
    onValueChange : ( value : Required<ParentComponentProps>['inputValue'] ) => void;

    log : Required<Required<ParentAllProps>['elusiveData']>['log']['input'];
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

type InternalState = {
    inputValue : string;
    inputType : InputType | '';
    inputDisableType : boolean;
    inputSchema : Schema | '';
    inputDisableSchema : boolean;
    inputXml : string;
    inputHasXmlTag : boolean;
    inputHasYooniteTag : boolean;
    inputHasTypeTag : boolean;

    errorData : {
        count : number;
        tooltip : string[];
        message : string[];
    };
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
};

type InternalProps = {
    type : InputType;
    schema : Schema;
    showInputErrorPopup : boolean;
};

const setParam = setParamOrig as <K extends keyof ( AllProps & InternalProps )>( panelKey : string, paramKey : K, paramValue : ( AllProps & InternalProps )[K] ) => ReturnType<typeof setParamOrig>;
const setElusiveData = setElusiveDataOrig as ( panelKey : string, data : Partial<PanelElusiveData> ) => ReturnType<typeof setElusiveDataOrig>;
const unsetElusiveData = unsetElusiveDataOrig as ( panelKey : string, keys : ( keyof PanelElusiveData )[] ) => ReturnType<typeof unsetElusiveDataOrig>;


const Input = ( props : AllProps ) => {
    const dispatch : AppDispatch        = useDispatch(),
          {
              uid,
              value,
              onValueChange,
              log
          }                             = props,
          user                          = useSelector( ( state : StoreState ) => state.session?.user ),
          language                      = user ? user.language : undefined,
          type                          = useSelector( ( state : StoreState ) => ( getSubPanelProps( uid, state )?.type ?? 'data' ) as InternalProps['type'] ),
          schema                        = useSelector( ( state : StoreState ) => ( getSubPanelProps( uid, state )?.schema ?? 'DATA' ) as InternalProps['schema'] ),
          showInputErrorPopup           = useSelector( ( state : StoreState ) => ( getSubPanelProps( uid, state )?.showInputErrorPopup ?? false ) as InternalProps['showInputErrorPopup'] ),
          esc                           = useCallback(
              ( text : string ) => {
                  return ( '' + text ).replace( /[\u00A0-\u9999<>\&]/g, char => '&#' + char.charCodeAt( 0 ) + ';' );
              },
              [
                  'onlyOnce'
              ]
          ),
          formatXmlToHtml               = useCallback(
              ( text : string, tabLength : number = 4, tabChar : string = '&nbsp;', lineBreak : string = '<br />\r\n' ) => {
                  const cdataContents : { [ cdataName : string ] : string } = {},
                        indent                                              = tabChar.repeat( tabLength ),
                        comment                                             = '<span class="comment">',
                        elementBracket                                      = '<span class="element-bracket">',
                        elementName                                         = '<span class="element-name">',
                        elementContent                                      = '<span class="element-content">',
                        attributeName                                       = '<span class="attribute-name">',
                        attributeValue                                      = '<span class="attribute-value">',
                        closer                                              = '</span>';

                  let retVal    = '',
                      matches : string[] | null,
                      indentSeq = '';

                  text = '' + text;

                  // replace all <!CDATA[...]]> sequences with ::cdata::number>:: placeholders
                  while ( ( matches = text.match( /<!\[CDATA\[(?:.|\n|\r|\u2028|\u2029)*?(?:]]>)/im ) ) ) {
                      const cdataName = `::cdata::${Object.keys( cdataContents ).length}::`;

                      cdataContents[ cdataName ] = matches[ 0 ];

                      text = text.replace( /<!\[CDATA\[(?:.|\n|\r|\u2028|\u2029)*?(?:]]>)/im, cdataName );
                  }

                  text = text.replaceAll( /(\r|\n)/g, '' );

                  const fullTextParts = text.split( />\s*/ );

                  for ( const textPart of fullTextParts ?? [] ) {
                      if ( textPart.trim() ) {
                          const nodes = textPart.split( /</ ),
                                node  = nodes[ nodes.length - 1 ].trim();

                          if ( nodes[ 0 ].trim() ) {
                              retVal += indentSeq + esc( nodes[ 0 ].trim() ) + closer + lineBreak;
                          }

                          const outerBrackets            = ( '<' + node + '>' ).match( /^(<[!?\/]?)(.*?)([?\/]?>)$/s ), // Split outer brackets
                                innerBrackets : string[] = outerBrackets?.[ 2 ] ? outerBrackets[ 2 ].match( /^(.*?)>(.*)<\/(.*)$/s ) ?? [ '', outerBrackets[ 2 ], '' ] : [], // Split inner brackets
                                attributes               = innerBrackets[ 1 ] ? innerBrackets[ 1 ].match( /^--.*--$|=|('|").*?\1|[^\t\n\f \/>"'=]+/g ) ?? [ '' ] : []; // Split attributes

                          // decrease indent
                          if ( outerBrackets?.[ 1 ] === '</' ) {
                              indentSeq = indentSeq.substring( indent.length );
                          }

                          // comment
                          if ( outerBrackets?.[ 1 ] === '<!' && attributes[ 0 ].indexOf( '--' ) === 0 ) {
                              retVal += indentSeq + comment + esc( outerBrackets[ 1 ] ?? '' ) + esc( attributes.join( '' ) ) + esc( outerBrackets[ 3 ] ?? '' ) + closer + lineBreak;
                          }
                          // all other
                          else {
                              retVal += indentSeq + elementBracket + esc( outerBrackets?.[ 1 ] ?? '' ) + closer;

                              retVal += elementName + esc( attributes[ 0 ] ) + closer;

                              for ( let i = 1; i < attributes.length; i++ ) {
                                  if ( attributes[ i ] === '=' ) {
                                      retVal += attributeValue + '=' + esc( attributes[ ++i ].replaceAll( '"', '\'' ) );
                                  }
                                  else {
                                      retVal += attributeName + ' ' + esc( attributes[ i ] );
                                  }
                                  retVal += closer;
                              }

                              retVal += innerBrackets[ 2 ] ? elementBracket + esc( '>' ) + closer + elementContent + esc( innerBrackets[ 2 ].trim() ) + closer + elementBracket + esc( '</' ) + closer + elementName + innerBrackets[ 3 ] + closer : '';

                              retVal += elementBracket + esc( outerBrackets?.[ 3 ] ?? '' ) + closer + lineBreak;

                              // increase indent
                              if ( '' + outerBrackets?.[ 1 ] + outerBrackets?.[ 3 ] + innerBrackets[ 2 ] === '<>' ) {
                                  indentSeq += indent;
                              }
                          }
                      }
                  }

                  retVal = retVal.substring( 0, retVal.length - lineBreak.length );

                  // replace all ::cdata::number>:: placeholders with <![CDATA[...]]> sequences
                  while ( retVal.match( /::cdata::[0-9]+::/gim ) ) {
                      retVal = retVal.replace( /::cdata::[0-9]+::/gim, ( placeholder ) => nl2br( esc( cdataContents[ placeholder ] ) ) );
                  }

                  return retVal;
              },
              [
                  esc
              ]
          ),
          formatXml                     = useCallback(
              ( text : string, tabLength : number = 4, tabChar : string = ' ', lineBreak : string = '\r\n' ) => {
                  let indent    = '',
                      formatted = '';

                  ( '' + text ).split( />\s*</ ).forEach( ( node ) => {
                      node = node.trim();

                      if ( node.match( /^\/\w/ ) ) {
                          indent = indent.substring( tabLength * tabChar.length );
                      }

                      formatted += `${indent}<${node}>${lineBreak}`;

                      if ( node.match( /^<?\w[^>]*[^\/]$/ ) ) {
                          indent += tabChar.repeat( tabLength );
                      }
                  } );

                  return formatted.substring( 1, formatted.length - 3 );
              },
              [
                  'onlyOnce'
              ]
          ),
          indent                        = useCallback(
              ( text : string, tabLength : number = 4, tabChar : string = '&nbsp;' ) => {
                  const indentSeq = tabChar.repeat( tabLength );

                  return `${indentSeq}${text.split( '\r\n' ).join( `\r\n${indentSeq}` )}`;
              },
              [
                  'onlyOnce'
              ]
          ),
          internalState : InternalState = useMemo(
              () => {
                  const retVal : InternalState = {
                      inputValue        : '',
                      inputType         : type,
                      inputDisableType  : false,
                      inputSchema       : schema,
                      inputDisableSchema: false,
                      inputXml          : '',
                      inputHasXmlTag    : false,
                      inputHasYooniteTag: false,
                      inputHasTypeTag   : false,

                      // error
                      errorData   : {
                          count  : 0,
                          tooltip: [],
                          message: []
                      },
                      errorMessage: undefined
                  };

                  if ( value ) {
                      const xmlParser  = new XmlParser(),
                            inputXml   = xmlParser.parse( value ) as Record<string, any>,
                            validation = XMLValidator.validate( value, {} );

                      if ( validation !== true && validation?.err.msg !== 'Multiple possible root nodes found.' ) {
                          const errorContextMaxLength = 80,
                                lineIndex             = validation.err.line - 1,
                                colIndex              = validation.err.col - 1,
                                valueLines            = value.split( '\n' ),
                                errorLine             = valueLines?.[ lineIndex ];

                          let positionErrorString : string | undefined = undefined;

                          if ( errorLine ) {
                              let contextOffset = 0,
                                  contextLength : number | undefined

                              if ( errorLine.length > errorContextMaxLength ) {
                                  if ( colIndex - errorContextMaxLength / 2 < 0 ) {
                                      contextLength = errorContextMaxLength - 3;
                                  }
                                  else if ( colIndex + errorContextMaxLength / 2 > errorLine.length ) {
                                      contextOffset = errorLine.length - ( errorContextMaxLength - 3 );
                                  }
                                  else {
                                      contextOffset = colIndex - ( errorContextMaxLength / 2 - 3 );
                                      contextLength = errorContextMaxLength - 6;
                                  }
                              }

                              positionErrorString = '<hr class="yn-error-ruler" /><div class="yn-error-message-monospace">';

                              if ( lineIndex > 0 ) {
                                  positionErrorString += '...<br />';
                              }
                              if ( contextOffset ) {
                                  positionErrorString += '...';
                              }

                              positionErrorString += errorLine.substring( contextOffset, contextLength ).replaceAll( ' ', '&nbsp;' );

                              if ( contextLength ) {
                                  positionErrorString += '...';
                              }

                              positionErrorString += `<br/>${'&nbsp;'.repeat( colIndex - ( contextOffset ? contextOffset - 3 : 0 ) )}<div class="yn-error-position-marker fas fa-arrow-up"></div>`;

                              if ( lineIndex < valueLines.length - 1 ) {
                                  positionErrorString += '<br />...';
                              }

                              positionErrorString += '</div>';
                          }

                          retVal.errorData.count++;

                          retVal.errorData.tooltip.push( `<div class="yn-tooltip-subheader">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.header' )}</div>` );
                          retVal.errorData.tooltip.push( positionErrorString ? __( 'YN.base.xmlImport.input.paste.error.invalidXml.positionError', { line: String( lineIndex + 1 ), col: String( colIndex + 1 ), positionError: positionErrorString } ) : __( 'YN.base.xmlImport.input.paste.error.invalidXml.text' ) );

                          if ( retVal.errorData!.message!.length ) {
                              retVal.errorData!.message!.push( '<hr class="yn-error-ruler"/>' );
                          }
                          retVal.errorData!.message!.push( `<div class="yn-error-subtitle">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.header' )}</div>` );
                          retVal.errorData!.message!.push( `<div class="yn-error-message">${positionErrorString ? __( 'YN.base.xmlImport.input.paste.error.invalidXml.positionError', { line: String( lineIndex + 1 ), col: String( colIndex + 1 ), positionError: positionErrorString } ) : ( 'YN.base.xmlImport.input.paste.error.invalidXml.text' )}</div>` );
                      }

                      let contentXml     = inputXml,
                          contentXmlKeys = Object.keys( contentXml ) as string[];

                      // remove ?xml tag
                      if ( contentXmlKeys.includes( '?xml' ) ) {
                          retVal.inputHasXmlTag = true;

                          contentXmlKeys.splice( contentXmlKeys.findIndex( k => k === '?xml' ), 1 );
                      }

                      // remove Yoonite tag
                      if ( contentXmlKeys.length === 1 && contentXmlKeys[ 0 ] === 'Yoonite' ) {
                          retVal.inputHasYooniteTag = true;

                          contentXml = inputXml[ contentXmlKeys[ 0 ] ];
                          contentXmlKeys = Object.keys( contentXml ) as string[];

                          // remove attributes (@_...)
                          contentXmlKeys = contentXmlKeys.filter( k => k.indexOf( '@_' ) !== 0 );

                          // remove info tag
                          if ( contentXmlKeys.includes( 'info' ) ) {
                              contentXmlKeys.splice( contentXmlKeys.findIndex( k => k === 'info' ), 1 );
                          }
                      }
                      else {
                          contentXml = xmlParser.parse( `<Yoonite>${value}</Yoonite>` )[ 'Yoonite' ] as Record<string, any>;
                          contentXmlKeys = Object.keys( contentXml ) as string[];
                      }

                      // only type tags
                      if ( contentXmlKeys.every( k => inputTypes.includes( k as InputType ) ) ) {
                          retVal.inputHasTypeTag = true;

                          retVal.inputType = contentXmlKeys.length === 1 ? contentXmlKeys[ 0 ] as InputType : '';
                          retVal.inputDisableType = true;

                          retVal.inputSchema = contentXml[ contentXmlKeys[ 0 ] ][ 0 ]?.[ '@_schema' ]?.toUpperCase() || '';
                          retVal.inputDisableSchema = true;

                          for ( const inputType of contentXmlKeys as InputType[] ) {
                              if ( retVal.inputSchema !== '' ) {
                                  if ( contentXml[ inputType ].length === 1 ) {
                                      const typeSchema = contentXml[ inputType ][ 0 ]?.[ '@_schema' ]?.toUpperCase();

                                      if ( retVal.inputSchema !== typeSchema ) {
                                          retVal.inputSchema = '';
                                      }
                                  }
                                  else {
                                      retVal.inputSchema = '';
                                  }
                              }

                              // TODO: validate
                          }
                      }

                      // only non-type tags
                      else if ( !contentXmlKeys.some( k => inputTypes.includes( k as InputType ) ) ) {
                          let validationResult : any | undefined = false;

                          // determine type (by validating it)
                          for ( const inputType of inputTypes ) {

                              if ( inputType === 'authorization' ) {
                                  /*validationResult = xmlValidator.validateAuthorization( xmlParser.parse( `<Yoonite><authorization>${inputValue}</authorization></Yoonite>` )[ 'Yoonite' ] );*/
                              }
                              else {
                                  const validationResults : {
                                      schema : Schema;
                                      result : any | undefined
                                  }[] = [ { schema: 'CFG', result: false } ];

                                  if ( inputType === 'metaModel' ) {
                                      /*validationResults = schemata.map( schema => {
                                          return {
                                              schema,
                                              result: xmlValidator.validateMetaModel( xmlParser.parse( `<Yoonite><metaModel schema='${schema.toLowerCase()}'>${inputValue}</metaModel></Yoonite>` )[ 'Yoonite' ] )
                                          };
                                      } );*/
                                  }
                                  else {
                                      /*validationResults = schemata.map( schema => {
                                          return {
                                              schema,
                                              result: xmlValidator.validateData( xmlParser.parse( `<Yoonite><data schema='${schema.toLowerCase()}'>${inputValue}</data></Yoonite>` )[ 'Yoonite' ]
                                          };
                                      } );*/
                                  }

                                  for ( const result of validationResults ) {
                                      if ( result.result === undefined ) {
                                          validationResult = undefined;

                                          retVal.inputSchema = schema;
                                          retVal.inputDisableSchema = !validationResults.every( r => r.result === undefined );

                                          break;
                                      }
                                  }
                              }

                              if ( validationResult === undefined ) {
                                  retVal.inputType = inputType;
                                  retVal.inputDisableType = true;

                                  if ( inputType === 'authorization' ) {
                                      retVal.inputSchema = '';
                                      retVal.inputDisableSchema = true;
                                  }

                                  break;
                              }
                          }

                          if ( validationResult !== undefined ) {
                              // TODO: error -> no type determinable
                          }
                      }

                      // mixed
                      else {
                          retVal.errorData.count++;

                          retVal.errorData.tooltip.push( `<div class="yn-tooltip-subheader">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.header' )}</div>` );
                          retVal.errorData.tooltip.push( __( 'YN.base.xmlImport.input.paste.error.invalidXml.text' ) );

                          if ( retVal.errorData!.message!.length ) {
                              retVal.errorData!.message!.push( '<hr class="yn-error-ruler"/>' );
                          }
                          retVal.errorData!.message!.push( `<div class="yn-error-subtitle">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.header' )}</div>` );
                          retVal.errorData!.message!.push( `<div class="yn-error-message">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.text' )}</div>` );
                      }

                      retVal.inputDisableSchema = ( retVal.inputDisableSchema ?? false ) || retVal.inputType === 'authorization';

                      // validate
                      for ( const key of contentXmlKeys ) {
                          const keyContent = Array.isArray( contentXml[ key ] )
                              ? contentXml[ key ]
                              : [ contentXml[ key ] ];

                          for ( const content of keyContent ) {
                              if ( content[ '#text' ] ) {
                                  retVal.errorData.count++;

                                  retVal.errorData.tooltip.push( `<div class="yn-tooltip-subheader">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.header' )}</div>` );
                                  retVal.errorData.tooltip.push( __( 'YN.base.xmlImport.input.paste.error.invalidXml.text' ) );

                                  if ( retVal.errorData!.message!.length ) {
                                      retVal.errorData!.message!.push( '<hr class="yn-error-ruler"/>' );
                                  }
                                  retVal.errorData!.message!.push( `<div class="yn-error-subtitle">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.header' )}</div>` );
                                  retVal.errorData!.message!.push( `<div class="yn-error-message">${__( 'YN.base.xmlImport.input.paste.error.invalidXml.text' )}</div>` );
                              }
                          }
                      }

                      // build inputValue and inputXml
                      retVal.inputValue = formatXmlToHtml( value );
                      retVal.inputXml = value;

                      //  wrap with type tag if not
                      if ( !retVal.inputHasTypeTag ) {
                          if ( retVal.inputType === 'authorization' ) {
                              retVal.inputValue = `<span class="p-disabled">${formatXmlToHtml( '<authorization>' )}</span><br />\r\n${indent( retVal.inputValue )}<br/>\r\n<span class="p-disabled">${formatXmlToHtml( '</authorization>' )}</span>`;
                              retVal.inputXml = `<authorization>${retVal.inputXml}</authorization>`;
                          }
                          else if ( retVal.inputType && retVal.inputSchema ) {
                              retVal.inputValue = `<span class="p-disabled">${formatXmlToHtml( `<${retVal.inputType} schema='${retVal.inputSchema.toLowerCase()}'>` )}</span><br />\r\n${indent( retVal.inputValue )}<br/>\r\n<span class="p-disabled">${formatXmlToHtml( `</${retVal.inputType}>` )}</span>`;
                              retVal.inputXml = `<${retVal.inputType} schema='${retVal.inputSchema.toLowerCase()}'>${retVal.inputXml}</${retVal.inputType}>`;
                          }
                      }
                      //  wrap with Yoonite tag if not
                      if ( !retVal.inputHasYooniteTag ) {
                          retVal.inputValue = `<span class="p-disabled">${formatXmlToHtml( '<Yoonite xmlns:xsi=\'http://www.w3.org/2001/XMLSchema-instance\' xsi:noNamespaceSchemaLocation=\'https://fsz.de/yoonite-tech/xml/v2/Yoonite.xsd\'>' )}</span><br />\r\n${indent( retVal.inputValue )}<br/>\r\n<span class="p-disabled">${formatXmlToHtml( '</Yoonite>' )}</span>`;
                          retVal.inputXml = `<Yoonite xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance' xsi:noNamespaceSchemaLocation='https://fsz.de/yoonite-tech/xml/v2/Yoonite.xsd'>${retVal.inputXml}</Yoonite>`;
                      }
                      //  prepend with ?xml tag if not
                      if ( !retVal.inputHasXmlTag ) {
                          retVal.inputValue = `<span class="p-disabled">${formatXmlToHtml( '<?xml version=\'1.0\' encoding=\'UTF-8\'?>' )}</span><br/>\r\n${retVal.inputValue}`;
                          retVal.inputXml = `<?xml version='1.0' encoding='UTF-8'?>${retVal.inputXml}`;
                      }

                      retVal.inputXml = formatXml( retVal.inputXml );
                  }

                  // errors
                  if ( retVal.errorData.count ) {
                      retVal.inputXml = '';
                      retVal.errorMessage = parse( retVal.errorData.message.join( '' ) );
                  }

                  return retVal;
              },
              [
                  formatXml,
                  formatXmlToHtml,
                  indent,
                  language,
                  schema,
                  type,
                  value
              ]
          ),
          onTypeChange                  = useCallback(
              ( newType : InputType ) => {
                  dispatch( setParam( uid, 'type', newType ) );
              },
              [
                  dispatch,
                  uid
              ]
          ),
          onSchemaChange                = useCallback(
              ( newSchema : Schema ) => {
                  dispatch( setParam( uid, 'schema', newSchema ) );
              },
              [
                  dispatch,
                  uid
              ]
          ),
          onKeyDown                     = useCallback(
              ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                  const key = event.nativeEvent.key;

                  // allow select all, copy, save, paste and cut
                  if ( !( event.nativeEvent.metaKey && ( key === 'a' || key === 'c' || key === 's' || key === 'v' || key === 'x' ) ) ) {
                      event.nativeEvent.preventDefault();
                      event.nativeEvent.stopPropagation();
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onPaste                       = useCallback(
              ( event : React.ClipboardEvent<HTMLDivElement> ) => {
                  const data = event.clipboardData.getData( 'text/plain' );

                  onValueChange( data );

                  // TODO: clear log

                  event.nativeEvent.preventDefault();
                  document.getSelection()?.empty();
              },
              [
                  onValueChange
              ]
          ),
          dialog : React.ReactNode      = useMemo(
              () => {
                  return <Dialog
                      key={`${uid}_inputErrorPopup`}

                      header={__( 'YN.base.xmlImport.input.error.title' )}
                      footer={<div><Button label="OK" className="p-button-outlined"
                                           onClick={() => dispatch( setParam( uid, 'showInputErrorPopup', false ) )}/></div>}
                      maskClassName="yn-dialog-panel-modal"
                      appendTo="self"
                      position="top"
                      visible={showInputErrorPopup}
                      style={{ width: 'auto', maxWidth: '80%' }}
                      closable={false}
                      className="yn-error-dialog"
                      onHide={() => {
                      }}>
                      {internalState.errorMessage}
                  </Dialog>
              },
              [
                  dispatch,
                  uid,
                  showInputErrorPopup,
                  internalState.errorMessage,
                  language
              ]
          ),
          toolbar : React.ReactNode     = useMemo(
              () => {
                  return <InputToolbar
                      uid={`${uid}_inputToolbar`}

                      type={internalState.inputType}
                      disableType={internalState.inputDisableType}
                      schema={internalState.inputSchema}
                      disableSchema={internalState.inputDisableSchema}

                      onTypeChange={onTypeChange}
                      onSchemaChange={onSchemaChange}
                  />
              },
              [
                  uid,
                  internalState.inputType,
                  internalState.inputDisableType,
                  internalState.inputSchema,
                  internalState.inputDisableSchema,
                  onTypeChange,
                  onSchemaChange
              ]
          ),
          input : React.ReactNode       = useMemo(
              () => {
                  return <div key={`${uid}_inputTextarea_panel`} className="yn-content-panel-container yn-flex-layout-parent"
                              style={{ flex: '1 1 auto' }}>
                      <Card className="yn-flex-layout-parent" style={{ flex: '1 1 auto' }}>
                          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                          <div
                              key={`${uid}_input`}
                              className="yn-xml yn-editable"
                              style={{ flex: 1, overflowX: 'hidden', overflowY: 'auto' }}

                              onKeyDown={onKeyDown}
                              onPaste={onPaste}

                              contentEditable={true}
                              suppressContentEditableWarning={true}
                          >
                              {!isInvalidHTML( internalState.inputValue ) ? parse( internalState.inputValue ) : 'nix da!'}
                          </div>
                      </Card>
                  </div>;
              },
              [
                  uid,
                  internalState.inputValue,
                  onKeyDown,
                  onPaste
              ]
          ),
          logPanel                      = useMemo(
              () => {
                  if ( log?.length ) {
                      const output : React.ReactNode[] = [],
                            indentLength : number      = 28;

                      for ( const logEntry of log ) {
                          let logEntryString = '';

                          logEntryString += `[${moment( logEntry.timestamp ).format( 'YYYY-MM-DD HH:mm:ss' )}]`;
                          logEntryString += `[<span class="yn-log-${logEntry.severity}">${logEntry.severity}</span>${'&nbsp;'.repeat( 5 - logEntry.severity.length )}]`;
                          logEntryString += `&nbsp;${logEntry.message.replaceAll( ' ', '&nbsp;' ).split( '\n' ).join( '\n<br/>' + '&nbsp;'.repeat( indentLength ) )}`;
                          logEntryString += '\n<br/>';

                          output.push( parse( logEntryString ) );
                      }

                      return <div
                          key={`${uid}_logPanel`}
                          className="yn-content-panel-container yn-flex-layout-parent"
                          style={{
                              paddingLeft: 0,
                              flex       : '0 0 50rem',
                              height     : '100%'
                          }}
                      >
                          <Card
                              style={{
                                  alignItems: 'stretch',
                                  display   : 'flex',
                                  flex      : 1,
                                  padding   : '1rem 2rem',
                                  height    : '100%'
                              }}
                              pt={{
                                  body   : {
                                      style: {
                                          padding: '0',
                                          width  : '100%'
                                      }
                                  },
                                  content: {
                                      style: {
                                          flex      : 1,
                                          overflow  : 'auto',
                                          whiteSpace: 'nowrap',
                                          height    : '100%',
                                          width     : '100%'
                                      }
                                  }
                              }}
                          >
                              <div className="yn-log">
                                  {output}
                              </div>
                              <div id={`${uid}_logPanel_bottom`} style={{ float: 'left', clear: 'both' }}/>
                          </Card>
                      </div>
                  }
                  else {
                      return null;
                  }
              },
              [
                  log
              ]
          );

    // scroll log to bottom
    useLayoutEffect(
        () => {
            document.getElementById( `${uid}_logPanel_bottom` )?.scrollIntoView( { behavior: 'smooth' } );
        }
    );

    useLayoutEffect(
        () => {
            if ( internalState.errorData.count ) {
                dispatch( setParam( uid, 'showInputErrorPopup', true ) );
                dispatch( setElusiveData( uid, {
                    errorData: {
                        count  : internalState.errorData.count,
                        tooltip: [ parse( internalState.errorData.tooltip.join( '' ) ) ],
                        message: [ parse( internalState.errorData.message.join( '' ) ) ]
                    }
                } ) );
            }
            else {
                dispatch( unsetElusiveData( uid, [ 'errorData' ] ) );
            }
        },
        [
            dispatch,
            uid,
            value,
            internalState.errorData.count,
            internalState.errorData.tooltip,
            internalState.errorData.message
        ]
    );

    useLayoutEffect(
        () => {
            dispatch( setElusiveData( uid, {
                xml: internalState.inputXml
            } ) );
        },
        [
            dispatch,
            uid,
            internalState.inputXml
        ]
    );

    return useMemo(
        () => {
            return <>
                {dialog}
                {toolbar}
                <div className="yn-flex-layout-parent yn-flex-layout-child flex-row align-items-stretch">
                    {input}
                    {logPanel}
                </div>
            </>;
        },
        [
            dialog,
            toolbar,
            input,
            logPanel
        ]
    );
};

Input.defaultProps = defaultProps;
Input.displayName = 'XmlImport-Input';

export default Input;
